// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// A persisted, per-device full-text-ish search index over decrypted message
// bodies, backed by IndexedDB. The homeserver only holds ciphertext, so search
// must be client-side; this lets it span ALL history this device has ever seen
// and decrypted — not just what's currently in memory.
//
// Privacy: message bodies are stored in plaintext in IndexedDB (local to this
// device/browser). Encrypting the index with a local key (à la Element/seshat)
// is a follow-up; cleared on logout via clearSearchIndex().

const DB_NAME = "zalmoxis-search";
const STORE = "messages";
const VERSION = 1;

export interface IndexedMessage {
  eventId: string;
  roomId: string;
  roomName: string;
  sender: string;
  body: string;
  ts: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "eventId" });
        store.createIndex("ts", "ts");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

// indexMessage upserts a message into the index (keyed by event ID, so re-indexing
// the same event — e.g. after decryption — is idempotent).
export async function indexMessage(m: IndexedMessage): Promise<void> {
  if (!m.body) return;
  try {
    const db = await open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(m);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* IndexedDB unavailable (private mode, etc.) — search falls back to memory */
  }
}

// searchMessages returns indexed messages whose body contains the query, newest
// first. A cursor scan + substring match — adequate for per-device history.
export async function searchMessages(query: string, limit = 50): Promise<IndexedMessage[]> {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  try {
    const db = await open();
    return await new Promise<IndexedMessage[]>((resolve, reject) => {
      const out: IndexedMessage[] = [];
      const tx = db.transaction(STORE, "readonly");
      // Walk the ts index in descending order so results are newest-first.
      const cursorReq = tx.objectStore(STORE).index("ts").openCursor(null, "prev");
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor || out.length >= limit) {
          resolve(out);
          return;
        }
        const m = cursor.value as IndexedMessage;
        if (m.body.toLowerCase().includes(q)) out.push(m);
        cursor.continue();
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
  } catch {
    return [];
  }
}

// clearSearchIndex wipes the index (called on logout so a shared device doesn't
// leak the previous user's decrypted history).
export async function clearSearchIndex(): Promise<void> {
  try {
    const db = await open();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* ignore */
  }
}
